import axios from 'axios';
import { ENV } from '@configs/env';
import { useAuthStore } from '@stores/auth.store';
import { refreshToken } from '@services/refresh.service';
import { useEdocSessionStore } from '@stores/edocSession.store';
import { refreshEdocPublicAccessToken } from '@services/edocPublic.service';

const normalizePath = (url = '') => {
    if (!url) return '';
    const withoutQuery = String(url).split('?')[0] || '';
    return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
};

const isPublicPath = (url = '') => {
    const path = normalizePath(url);
    return ['/auth/login', '/auth/register', '/auth/refresh'].some(
        (p) => path === p || path.startsWith(`${p}/`)
    );
};

const isEdocPublicPath = (url = '') => {
    const path = normalizePath(url);
    return path === '/tenant/edocs-platform/public/me'
        || path === '/tenant/edocs-platform/public/auth/refresh'
        || path.startsWith('/tenant/edocs-platform/public/');
};

const http = axios.create({
    baseURL: ENV.API_URL,
    withCredentials: false
});

let refreshPromise = null;
let edocRefreshPromise = null;
let interceptorsInitialized = false;

export const initializeHttpInterceptors = () => {
    if (interceptorsInitialized) return;
    interceptorsInitialized = true;

    http.interceptors.request.use((config) => {
        const url = config.url || '';
        const authState = useAuthStore.getState();
        const edocState = useEdocSessionStore.getState();

        // Prefer app auth token for non-public-edoc endpoints.
        const appToken = authState.accessToken;
        const edocToken = edocState.accessToken;

        if (isEdocPublicPath(url)) {
            if (edocToken) config.headers.Authorization = `Bearer ${edocToken}`;
        } else if (appToken) {
            config.headers.Authorization = `Bearer ${appToken}`;
        }
        return config;
    });

    http.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            const requestUrl = originalRequest?.url || '';
            const isAuthPublicRequest = isPublicPath(requestUrl);
            const isRefreshCall = normalizePath(requestUrl) === '/auth/refresh';
            const isEdocPublicRequest = isEdocPublicPath(requestUrl);
            const isEdocRefreshCall = normalizePath(requestUrl) === '/tenant/edocs-platform/public/auth/refresh';

            if (error.response?.status === 401 && (isAuthPublicRequest || isRefreshCall)) {
                return Promise.reject(error);
            }

            if (error.response?.status === 401 && isEdocPublicRequest && isEdocRefreshCall) {
                useEdocSessionStore.getState().clear();
                return Promise.reject(error);
            }

            if (error.response?.status === 401 && !originalRequest?._retry) {
                originalRequest._retry = true;
                originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

                if (originalRequest._retryCount > 3) {
                    if (isEdocPublicRequest) {
                        useEdocSessionStore.getState().clear();
                    } else {
                        useAuthStore.getState().logout();
                    }
                    return Promise.reject(error);
                }

                if (isEdocPublicRequest) {
                    if (!edocRefreshPromise) {
                        edocRefreshPromise = (async () => {
                            try {
                                const refreshTokenValue = useEdocSessionStore.getState().refreshToken;
                                if (!refreshTokenValue) return Promise.reject(error);
                                const next = await refreshEdocPublicAccessToken(refreshTokenValue);
                                if (!next?.accessToken) {
                                    useEdocSessionStore.getState().clear();
                                    return Promise.reject(new Error('Refresh failed'));
                                }
                                useEdocSessionStore.getState().setSession({
                                    accessToken: next.accessToken,
                                    // refresh may not rotate in this API; keep existing if not provided
                                    refreshToken: next.refreshToken || refreshTokenValue,
                                    accessExpiresAt: next.accessExpiresAt ?? null,
                                    refreshExpiresAt: next.refreshExpiresAt ?? null
                                });
                                return { accessToken: next.accessToken };
                            } finally {
                                edocRefreshPromise = null;
                            }
                        })();
                    }
                    try {
                        const next = await edocRefreshPromise;
                        originalRequest.headers.Authorization = `Bearer ${next.accessToken}`;
                        return http(originalRequest);
                    } catch (err) {
                        return Promise.reject(err);
                    }
                }

                if (!refreshPromise) {
                    refreshPromise = (async () => {
                        try {
                            const refreshTokenValue = useAuthStore.getState().refreshToken;
                            const setAuthState = useAuthStore.setState;
                            const logout = useAuthStore.getState().logout;

                            if (!refreshTokenValue) {
                                return Promise.reject(error);
                            }

                            const newTokens = await refreshToken(
                                refreshTokenValue,
                                setAuthState,
                                logout
                            );

                            if (!newTokens || !newTokens.accessToken) {
                                logout();
                                return Promise.reject(new Error('Refresh failed'));
                            }

                            return newTokens;
                        } catch (err) {
                            useAuthStore.getState().logout();
                            return Promise.reject(err);
                        } finally {
                            refreshPromise = null;
                        }
                    })();
                }

                try {
                    const newTokens = await refreshPromise;
                    originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
                    return http(originalRequest);
                } catch (err) {
                    return Promise.reject(err);
                }
            }

            return Promise.reject(error);
        }
    );
};

export default http;
